(() => {
  'use strict';

  function readRulePack(configurator) {
    const source = configurator.querySelector('[data-universal-rule-pack]');
    if (!source) return null;

    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      return null;
    }
  }

  function selectedOptions(configurator) {
    return Array.from(configurator.querySelectorAll('[data-universal-option]')).map((option) => {
      const input = option.querySelector('[data-universal-option-value]:checked');
      const label = input?.closest('label')?.textContent.trim() || input?.value || '';
      const selected = {
        name: option.dataset.optionName || option.querySelector('legend')?.textContent.trim() || 'Option',
        label,
        value: input?.value || ''
      };
      const position = Number(option.dataset.optionPosition);
      if (Number.isInteger(position) && position > 0) {
        Object.defineProperty(selected, 'position', { value: position });
      }
      return selected;
    });
  }

  function readNativeVariants(configurator) {
    const source = configurator.querySelector('[data-universal-native-variants]');
    if (!source) return null;

    try {
      const variants = JSON.parse(source.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      return [];
    }
  }

  function matchesNativeVariant(configuration, variants) {
    if (variants === null) return true;
    return variants.some((variant) => {
      if (variant?.available === false || !Array.isArray(variant?.options)) return false;
      return configuration.options.every((option, index) => {
        const position = Number.isInteger(option.position) ? option.position : index + 1;
        return variant.options[position - 1] === option.value;
      });
    });
  }

  function readConfiguration(configurator) {
    const value = (selector) => configurator.querySelector(selector)?.value?.trim() || '';
    const depthContainer = configurator.querySelector('[data-depth-confirmation]');
    const depthControl = depthContainer?.checked === undefined
      ? depthContainer?.querySelector('input[type="checkbox"]')
      : depthContainer;
    return {
      options: selectedOptions(configurator),
      width: {
        whole: value('[data-width-whole]'),
        fraction: value('[data-width-fraction]')
      },
      height: {
        whole: value('[data-height-whole]'),
        fraction: value('[data-height-fraction]')
      },
      quantity: value('[data-quantity]'),
      depthConfirmed: Boolean(depthControl?.checked),
      room: value('[data-universal-room]'),
      note: value('[data-universal-note]')
    };
  }

  function isWholeInRange(value, range) {
    const number = Number(value);
    return Number.isInteger(number) && number >= range.min && number <= range.max;
  }

  function validate(configuration, rules) {
    const dimensions = rules?.dimensions;
    const fractions = dimensions?.fractions;
    if (
      !dimensions ||
      !Number.isInteger(dimensions.width?.min) ||
      !Number.isInteger(dimensions.width?.max) ||
      !Number.isInteger(dimensions.height?.min) ||
      !Number.isInteger(dimensions.height?.max) ||
      !Array.isArray(fractions)
    ) {
      return false;
    }

    const quantity = Number(configuration.quantity);
    return configuration.options.every((option) => option.value && option.label) &&
      isWholeInRange(configuration.width.whole, dimensions.width) &&
      fractions.includes(configuration.width.fraction) &&
      isWholeInRange(configuration.height.whole, dimensions.height) &&
      fractions.includes(configuration.height.fraction) &&
      Number.isInteger(quantity) && quantity > 0 &&
      (!rules.requiresDepthConfirmation || configuration.depthConfirmed);
  }

  function displaySize(dimension) {
    return dimension.fraction === '0'
      ? dimension.whole
      : `${dimension.whole} ${dimension.fraction}`;
  }

  function renderProgress(form, activeStep) {
    form.querySelectorAll('[data-progress-step]').forEach((item) => {
      const active = item.dataset.progressStep === activeStep;
      item.toggleAttribute('aria-current', active);
      if (active) item.setAttribute('aria-current', 'step');
    });
  }

  function renderReview(reviewList, configuration, valid, variantMatches) {
    const rows = valid
      ? [
        ...configuration.options.map((option) => [option.name, option.label]),
        ['Width', displaySize(configuration.width)],
        ['Height', displaySize(configuration.height)],
        ['Quantity', configuration.quantity],
        ...(configuration.depthConfirmed ? [['Mounting depth', 'Confirmed']] : []),
        ...(configuration.room ? [['Room', configuration.room]] : []),
        ...(configuration.note ? [['Note', configuration.note]] : [])
      ]
      : [[
        'Configuration',
        variantMatches
          ? 'Complete the required options and size fields to preview.'
          : 'Choose an option combination that matches an available variant.'
      ]];

    reviewList.replaceChildren(...rows.flatMap(([term, description]) => {
      const termNode = document.createElement('dt');
      const descriptionNode = document.createElement('dd');
      termNode.textContent = term;
      descriptionNode.textContent = description;
      return [termNode, descriptionNode];
    }));
  }

  function initializeFabricFilters(configurator) {
    const filters = Array.from(configurator.querySelectorAll('[data-universal-fabric-filter]'));
    const choices = Array.from(configurator.querySelectorAll('[data-universal-fabric-option]'));
    if (!filters.length || !choices.length) return;

    const select = (group) => {
      choices.forEach((choice) => {
        choice.hidden = group !== 'all' && choice.dataset.universalFabricGroup !== group;
      });
      filters.forEach((button) => {
        const active = button.dataset.universalFabricFilter === group;
        if (button.classList) button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    filters.forEach((button) => {
      button.addEventListener('click', () => {
        select(button.dataset.universalFabricFilter);
        renderProgress(configurator, 'fabric');
      });
    });
    select('all');
  }

  function initialize(configurator) {
    const reviewList = configurator.querySelector('[data-review-list]');
    const configurationResult = configurator.querySelector('[data-configuration-result]');
    const finalAction = configurator.querySelector('[data-final-action]');
    if (!reviewList || !configurationResult || !finalAction) return;

    finalAction.disabled = true;
    initializeFabricFilters(configurator);
    const rules = readRulePack(configurator);
    const nativeVariants = readNativeVariants(configurator);
    const sync = (event) => {
      const configuration = readConfiguration(configurator);
      const variantMatches = matchesNativeVariant(configuration, nativeVariants);
      const valid = validate(configuration, rules) && variantMatches;
      renderReview(reviewList, configuration, valid, variantMatches);
      configurationResult.value = valid ? JSON.stringify(configuration) : '';
      const activeStep = valid
        ? 'review'
        : event?.target?.closest?.('[data-customizer-step]')?.dataset.customizerStep ||
          configurator.querySelector('[data-progress-step][aria-current]')?.dataset.progressStep ||
          'fabric';
      renderProgress(configurator, activeStep);
      finalAction.disabled = true;
    };

    configurator.addEventListener('input', sync);
    configurator.addEventListener('change', sync);
    sync();
  }

  document.addEventListener('DOMContentLoaded', () => {
    const configurators = document.querySelectorAll('[data-universal-shade-configurator]');
    if (!configurators.length) return;
    configurators.forEach(initialize);
  });
})();
